import React, { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n";
import { fetchPaymentState } from "@/lib/api";
import { openExternalLink, haptic } from "@/lib/telegram";
import type { Order, PaymentState } from "@/types";
import { DiamondBurst } from "./DiamondBurst";

const POLL_MS = 5000;
const MAX_POLL_MS = 45 * 60 * 1000;

/**
 * "Waiting for payment" block for online orders. Checks the server every 5 s
 * and immediately when the customer comes back from the Payme/Click page
 * (Telegram fires visibilitychange on return), so the screen flips to
 * "Оплата получена" without the customer doing anything.
 */
export function PaymentStatus({ order, onSettled }: { order: Order; onSettled?: () => void }) {
  const { t, locale } = useI18n();
  const [state, setState] = useState<PaymentState | null>(null);
  const [checking, setChecking] = useState(false);
  const startedAt = useRef(Date.now());
  const settledRef = useRef(false);

  const isOnline = order.paymentMethod !== "cash_on_pickup";
  const needsWatching = isOnline && order.paymentStatus !== "paid" && order.status !== "cancelled";

  const check = useCallback(async () => {
    setChecking(true);
    try {
      const next = await fetchPaymentState(order.id);
      setState(next);
      const settled = next.paymentStatus === "paid" || next.status === "cancelled";
      if (settled && !settledRef.current) {
        settledRef.current = true;
        haptic(next.paymentStatus === "paid" ? "success" : "warning");
        onSettled?.();
      }
    } catch {
      /* transient network error — next tick retries */
    } finally {
      setChecking(false);
    }
  }, [order.id, onSettled]);

  useEffect(() => {
    if (!needsWatching) return;
    check();
    const timer = setInterval(() => {
      if (settledRef.current || Date.now() - startedAt.current > MAX_POLL_MS) return;
      check();
    }, POLL_MS);
    const onVisible = () => document.visibilityState === "visible" && !settledRef.current && check();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [needsWatching, check]);

  if (!isOnline) return null;

  const paid = (state?.paymentStatus ?? order.paymentStatus) === "paid";
  const cancelled = (state?.status ?? order.status) === "cancelled";

  if (paid) {
    return (
      <div className="pay-status pay-status--ok" style={{ position: "relative" }}>
        {settledRef.current && <DiamondBurst />}
        <strong>{t("payment.paidTitle")}</strong>
        <span>{t("payment.paidBody")}</span>
      </div>
    );
  }
  if (cancelled) {
    return (
      <div className="pay-status pay-status--bad">
        <strong>{t("payment.expiredTitle")}</strong>
        <span>{t("payment.expiredBody")}</span>
      </div>
    );
  }

  const deadline = new Date(state?.paymentDeadline ?? order.paymentDeadline);
  const time = new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Tashkent" }).format(deadline);

  return (
    <div className="pay-status">
      <strong>{t("payment.waitingTitle")}</strong>
      <span>{t("payment.waitingBody", { time })}</span>
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        {state?.payable && state.checkoutUrl && (
          <button className="btn btn--primary" style={{ width: "auto", flex: 1 }} onClick={() => openExternalLink(state.checkoutUrl!)}>
            {t("payment.payButton")}
          </button>
        )}
        <button className="btn btn--ghost" onClick={check} disabled={checking}>
          {checking ? t("payment.checking") : t("payment.refresh")}
        </button>
      </div>
    </div>
  );
}
